import React, { useEffect, useState } from "react";
import { Moon, Sun } from "lucide-react";
import { toggleTheme, getActiveTheme, type Theme } from "../lib/theme";

interface ThemeToggleProps {
  className?: string;
}

/**
 * Light/dark switch. Renders BOTH icons; CSS (styles.css `.theme-toggle-*`,
 * keyed off `html.dark`) shows the one for the theme you'd switch TO, so the
 * correct icon paints before hydration with zero flash and no SSR mismatch.
 *
 * `theme` state only drives aria-pressed. SSR + first client render assume the
 * light default (matching the server markup); a mount effect then syncs to what
 * the anti-FOUC script actually applied, so a dark-mode visitor's toggle reports
 * the right pressed-state without a hydration warning.
 */
const ThemeToggle: React.FC<ThemeToggleProps> = ({ className = "" }) => {
  const [theme, setTheme] = useState<Theme>("light");

  useEffect(() => {
    setTheme(getActiveTheme());
  }, []);

  return (
    <button
      type="button"
      onClick={() => setTheme(toggleTheme())}
      aria-label="Toggle light or dark mode"
      aria-pressed={theme === "dark"}
      title="Toggle light or dark mode"
      className={`theme-toggle inline-flex items-center justify-center rounded-full text-ink hover:bg-salmon/10 hover:text-salmon transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-salmon ${className}`}
    >
      <Sun
        size={18}
        strokeWidth={2}
        aria-hidden="true"
        className="theme-toggle-sun"
      />
      <Moon
        size={18}
        strokeWidth={2}
        aria-hidden="true"
        className="theme-toggle-moon"
      />
    </button>
  );
};

export default ThemeToggle;
